import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import Navbar from "react-bootstrap/Navbar";
import Container from "react-bootstrap/Container";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Dropdown from "react-bootstrap/Dropdown";
import authApi from "../../data/api/authApi";
import { NavLink, useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import {
  getAllClients,
  getAllUsers,
  getAllWorks,
  getUser
} from "../../data/getters";
import { useDispatch } from "react-redux";
import { useLanguage } from "../helpers/LanguageContext";
import { useNavigationGuard } from "../helpers/NavigationGuardContext";
import "../../App.css";
import "../../css/TopNavigationBar.css";
function TopNavigationBar() {
  const navbarRef = useRef(null);
  useLayoutEffect(() => {
    const navbar = navbarRef.current;
    const style = document.documentElement.style;
    const updateHeight = () =>
      style.setProperty(
        "--top-navigation-height",
        `${navbar.getBoundingClientRect().height}px`
      );
    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(navbar);
    return () => {
      observer.disconnect();
      style.removeProperty("--top-navigation-height");
    };
  }, []);
  const location = useLocation();
  const workMatch = location.pathname.match(/^\/work\/([^/]+)/);
  const workId = workMatch?.[1];
  const isWorkRoute = Boolean(workId);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { guardedNavigate } = useNavigationGuard();
  useEffect(() => {
    if (!workId) return;
    const tabs = [
      `/work/${workId}`,
      `/work/${workId}/model`,
      `/work/${workId}/tables`,
      `/work/${workId}/items`
    ];
    const handleKeyDown = (event) => {
      if (
        !event.ctrlKey ||
        event.shiftKey ||
        event.altKey ||
        event.metaKey ||
        !["ArrowLeft", "ArrowRight"].includes(event.key)
      ) {
        return;
      }
      // A lokális vezérlők elsőbbséget élveznek a globális tabváltással szemben.
      if (event.defaultPrevented) return;
      // Nyitott szín-/marásválasztó vagy egyéb dialógus alatt nincs tabváltás.
      if (
        document.querySelector(
          '.modal.show, [role="dialog"][aria-modal="true"]'
        )
      ) {
        return;
      }
      // Csak a négy megadott tabon működjön
      const currentIndex = tabs.indexOf(location.pathname);
      if (currentIndex === -1) return;
      // A szélső taboknál ne lépjen tovább
      const nextIndex =
        event.key === "ArrowRight" ? currentIndex + 1 : currentIndex - 1;
      if (nextIndex < 0 || nextIndex >= tabs.length) return;
      event.preventDefault();
      guardedNavigate(tabs[nextIndex]);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [workId, location.pathname, guardedNavigate]);
  const [showModal, setShowModal] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [username, setUsername] = useState("");
  useEffect(() => {
    const fetchUser = async () => {
      const userData = await dispatch(getUser());
      setIsAdmin(userData.role === "admin");
      setUsername(userData.username || "");
    };
    fetchUser();
  }, [dispatch]);
  const { language, setLanguage, t } = useLanguage();
  const handleLanguageChange = (langCode) => {
    setLanguage(langCode);
  };
  useEffect(() => {
    localStorage.setItem("lang", language);
  }, [language]);
  const LANGUAGES = [
    {
      code: "hu",
      label: "Magyar",
      flagSrc: "https\://flagcdn.com/w40/hu.png"
    },
    {
      code: "ro",
      label: "Română",
      flagSrc: "https\://flagcdn.com/w40/ro.png"
    },
    {
      code: "en",
      label: "English",
      flagSrc: "https\://flagcdn.com/w40/us.png"
    }
  ];
  const handleLogout = async () => {
    try {
      await authApi.logoutApi(); // token törlés/stb.
    } catch (e) {
      console.error("Logout hiba:", e);
    } finally {
      navigate("/login", { replace: true }); // irány a /login
    }
  };
  const handleCloseModal = () => setShowModal(false);
  const handleConfirmLogout = async () => {
    setShowModal(false);
    await handleLogout();
  };
  const isTablet = window.matchMedia(
    "(min-width: 769px) and (max-width: 1300px)"
  ).matches;
  const handleSearch = async (query) => {
    if (!query) {
      setSearchResults([]);
      return;
    }
    let clients = await dispatch(getAllClients());
    let works = await dispatch(getAllWorks());
    let users = isAdmin ? await dispatch(getAllUsers()) : [];
    // Ensure the data returned is in array format
    clients = Array.isArray(clients) ? clients : [];
    works = Array.isArray(works) ? works : [];
    users = Array.isArray(users) ? users : [];
    let results = [
      ...clients
        .filter(
          (client) =>
            client.name &&
            client.name.toLowerCase().includes(query.toLowerCase())
        )
        .map((client) => ({ ...client, type: "Client" })),
      ...works
        .filter(
          (work) =>
            work.title && work.title.toLowerCase().includes(query.toLowerCase())
        )
        .map((work) => ({ ...work, type: "Work" })),
      ...users
        .filter(
          (user) =>
            user.name && user.name.toLowerCase().includes(query.toLowerCase())
        )
        .map((user) => ({ ...user, type: "User" }))
    ];
    setSearchResults(results);
  };
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    handleSearch(e.target.value);
  };
  return (
    <>
      <Navbar
        ref={navbarRef}
        expand="lg"
        sticky="top"
        style={{
          backgroundColor: "#E9E7F1",
          userSelect: "none",
          WebkitUserSelect: "none",
          height: isTablet ? "5dvh" : "5dvh",
          minHeight: isTablet ? "5dvh" : "5dvh"
        }}
      >
        <Container fluid>
          <Navbar.Text className="me-3">
            {t("welcome")},{" "}
            <span style={{ fontWeight: "bold" }}>{username}</span>
          </Navbar.Text>
          <Navbar.Toggle aria-controls="navbarScroll" />
          <Navbar.Collapse id="navbarScroll">
            <Container
              className="  position-relative "
              style={{
                margin: "auto"
              }}
            >
              <div
                className="d-flex "
                style={{ width: "600px", margin: "auto", height: "4dvh" }}
              >
                <Form className="d-flex">
                  <Form.Control
                    type="search"
                    placeholder={t("search")}
                    //className="me-2"
                    aria-label="Search"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    style={{
                      width: "600px",
                      fontSize: "0.9rem",
                      userSelect: "text",
                      WebkitUserSelect: "text"
                    }}
                  />
                  {/*<Button variant="outline-success">Search</Button>*/}
                </Form>
              </div>
              {searchResults.length > 0 && (
                <Container
                  className="position-absolute"
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "100%"
                  }}
                >
                  <Dropdown.Menu
                    show
                    className="mt-2"
                    style={{
                      position: "absolute",
                      top: "0",
                      left: "49%",
                      transform: "translateX(-50%)",
                      width: "600px",
                      textAlign: "left",
                      maxHeight: "50dvh", // Maximális magasság beállítása
                      overflowY: "auto", // Görgethetőség engedélyezése
                      scrollBehavior: "smooth",
                      backgroundColor: "white",
                      fontSize: "18px"
                    }}
                  >
                    {searchResults.map((result, index) => (
                      <Dropdown.Item key={index} href="#">
                        {result.name || result.title || result.email}{" "}
                        <em style={{ fontSize: "0.8em", color: "#6c757d" }}>
                          ({result.type})
                        </em>
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Container>
              )}
            </Container>
            <div>
              <div className="d-flex align-items-center top-navbar-actions">
                {" "}
                {isWorkRoute && (
                  <div>
                    <Container
                      fluid
                      style={{
                        display: "flex",
                        gap: "2rem",
                        alignItems: "center",
                        fontSize: "0.85rem",
                        fontWeight: 500,
                        marginRight: "1rem",
                        alignContent: "center"
                      }}
                    >
                      {[
                        { to: `/work/${workId}`, label: t("overview") },
                        { to: `/work/${workId}/model`, label: t("model") },
                        { to: `/work/${workId}/tables`, label: t("tables") },
                        { to: `/work/${workId}/items`, label: t("dimensions") }
                      ].map(({ to, label }) => (
                        <NavLink
                          key={to}
                          to={to}
                          onClick={(event) => {
                            if (
                              event.ctrlKey ||
                              event.metaKey ||
                              event.shiftKey ||
                              event.altKey
                            )
                              return;
                            event.preventDefault();
                            if (location.pathname !== to) guardedNavigate(to);
                          }}
                          end
                          style={({ isActive }) => ({
                            color: isActive ? "#0d6efd" : "#495057",
                            fontWeight: isActive ? 600 : 500
                          })}
                          className={({ isActive }) =>
                            `work-tab ${isActive ? "active" : ""}`
                          }
                        >
                          {({ isActive }) => (
                            <span
                              style={{
                                position: "relative"
                              }}
                            >
                              {label}
                              <span
                                style={{
                                  transform: isActive
                                    ? "scaleX(1)"
                                    : "scaleX(0)"
                                }}
                                className="work-tab-underline"
                              />
                            </span>
                          )}
                        </NavLink>
                      ))}
                    </Container>
                  </div>
                )}
                {/* Nyelvválasztó */}
                <Dropdown align="end" className="language-dropdown">
                  {" "}
                  <Dropdown.Toggle as="div" className="lang-toggle">
                    <img
                      src={LANGUAGES.find((l) => l.code === language)?.flagSrc}
                      alt={language}
                      className="language-flag"
                    />
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {LANGUAGES.map((lang) => (
                      <Dropdown.Item
                        key={lang.code}
                        onClick={() => handleLanguageChange(lang.code)}
                      >
                        <img
                          src={lang.flagSrc}
                          alt={lang.code}
                          className="language-flag"
                        />
                        {lang.label}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
                <Button
                  variant="outline-danger"
                  className="logout-button"
                  onClick={() => setShowModal(true)}
                >
                  <i className="bi bi-box-arrow-right"></i>
                </Button>
              </div>
            </div>
          </Navbar.Collapse>
        </Container>
      </Navbar>
      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>{t("logoutTitle")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{t("logoutBody")}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            {t("cancel")}
          </Button>
          <Button variant="primary" onClick={handleConfirmLogout}>
            {t("logout")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
export default TopNavigationBar;
